/**
 * Utility functions for handling markdown text parsing and length calculations
 */

const MARKDOWN_LINK_PATTERN = /\[([^\]]*)\]\(([^)]+)\)/g;
const MARKDOWN_LINK_PATTERN_ANCHORED = /^\[([^\]]*)\]\([^)]+\)/;
const MARKDOWN_LINK_PATTERN_WITH_CONTEXT =
  /^([\s\S]*?)\[([^\]]*)\]\(([^)]+)\)([\s\S]*)/;
const ANGLE_BRACKET_LINK_PATTERN_WITH_CONTEXT =
  /^([\s\S]*?)<(https?:\/\/[^>]+)>([\s\S]*)/;

export function stripMarkdownToText(markdown: string): string {
  if (!markdown) return '';

  return (
    markdown
      // Remove headers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold/italic
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(\*|_)(.*?)\1/g, '$2')
      // Remove links [text](url) -> text
      .replace(MARKDOWN_LINK_PATTERN, '$1')
      // Remove inline code
      .replace(/`([^`]+)`/g, '$1')
      // Remove code blocks
      .replace(/```[\s\S]*?```/g, '')
      // Remove horizontal rules
      .replace(/^[-*_]{3,}$/gm, '')
      // Remove list markers
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // Clean up extra whitespace
      .replace(/\n\s*\n/g, '\n')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\s+/g, ' ')
  );
}

export function getMarkdownTextLength(markdown: string): number {
  return stripMarkdownToText(markdown).length;
}

export function truncateMarkdownAware(
  markdown: string,
  maxLength: number,
  suffix = '...'
): string {
  if (!markdown) return '';

  const plainTextLength = getMarkdownTextLength(markdown);

  if (plainTextLength <= maxLength) {
    return markdown;
  }

  // Binary search to find the optimal truncation point
  let left = 0;
  let right = markdown.length;
  let bestTruncation = '';

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const candidate = markdown.substring(0, mid);
    const candidateLength = getMarkdownTextLength(candidate);

    if (candidateLength <= maxLength - suffix.length) {
      bestTruncation = candidate;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  // Try to truncate at word boundary if possible
  const wordBoundaryRegex = /^(.*)\s+\S*$/;
  const wordBoundaryMatch = wordBoundaryRegex.exec(bestTruncation);
  if (wordBoundaryMatch && wordBoundaryMatch[1]) {
    const wordBoundaryTruncation = wordBoundaryMatch[1];
    const wordBoundaryLength = getMarkdownTextLength(wordBoundaryTruncation);

    if (wordBoundaryLength >= (maxLength - suffix.length) * 0.8) {
      bestTruncation = wordBoundaryTruncation;
    }
  }

  return bestTruncation.trim() + suffix;
}

export function isMarkdownTooLong(
  markdown: string,
  maxLength: number
): boolean {
  return getMarkdownTextLength(markdown) > maxLength;
}

const parseTextFormatting = (text: string) => {
  if (!text) return [];

  const parts = [];
  let remaining = text;

  const boldRegex = /^(.*?)\*\*([^*]+)\*\*(.*)/;
  const italicRegex = /^(.*?)(?<!\*)\*([^*]+)\*(?!\*)(.*)/;

  while (remaining) {
    const boldMatch = boldRegex.exec(remaining);
    const italicMatch = italicRegex.exec(remaining);

    if (
      boldMatch &&
      (!italicMatch || (boldMatch.index || 0) <= (italicMatch.index || 0))
    ) {
      const [, before, boldText, after] = boldMatch;
      if (before) parts.push({ type: 'text', content: before });
      parts.push({ type: 'bold', content: boldText });
      remaining = after;
    } else if (italicMatch) {
      const [, before, italicText, after] = italicMatch;
      if (before) parts.push({ type: 'text', content: before });
      parts.push({ type: 'italic', content: italicText });
      remaining = after;
    } else {
      parts.push({ type: 'text', content: remaining });
      break;
    }
  }

  return parts;
};

const findNextLink = (text: string) => {
  const standardLinkMatch = MARKDOWN_LINK_PATTERN_WITH_CONTEXT.exec(text);
  const angleBracketLinkMatch =
    ANGLE_BRACKET_LINK_PATTERN_WITH_CONTEXT.exec(text);

  const standardIndex = standardLinkMatch
    ? standardLinkMatch.index || 0
    : Infinity;
  const angleIndex = angleBracketLinkMatch
    ? angleBracketLinkMatch.index || 0
    : Infinity;

  if (standardLinkMatch && standardIndex <= angleIndex) {
    const [, before, linkText, url, after] = standardLinkMatch;
    return { before, linkText, url, after, type: 'standard' };
  }

  if (angleBracketLinkMatch && angleIndex < standardIndex) {
    const [, before, url, after] = angleBracketLinkMatch;
    return { before, linkText: url, url, after, type: 'angle' };
  }

  return null;
};

export const parseMarkdown = (text: string) => {
  const parts = [];
  let remaining = text;

  while (remaining) {
    const linkMatch = findNextLink(remaining);

    if (linkMatch) {
      const { before, linkText, url, after } = linkMatch;

      if (before) parts.push({ type: 'text', content: before });
      parts.push({ type: 'link', text: linkText || '', url: url || '' });
      remaining = after;
    } else {
      parts.push({ type: 'text', content: remaining });
      break;
    }
  }

  return parts
    .map((part) => {
      if (part.type === 'text') {
        return parseTextFormatting(part.content || '');
      }
      return { ...part, text: parseTextFormatting(part.text!) };
    })
    .flat();
};

export const getPlainTextLength = (text: string): number => {
  return text
    .replace(MARKDOWN_LINK_PATTERN, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '$1').length;
};

export const MAX_URL_LENGTH = 2048;

export function extractUrlsFromMarkdown(markdown: string): string[] {
  const urls: string[] = [];
  let remaining = markdown;

  let linkMatch = findNextLink(remaining);
  while (linkMatch) {
    urls.push(linkMatch.url);
    remaining = linkMatch.after;
    linkMatch = findNextLink(remaining);
  }

  return urls;
}

export const truncateAtVisibleLength = (
  text: string,
  maxLength: number
): string => {
  let visibleLength = 0;
  let i = 0;

  const boldRegex = /^\*\*([^*]+)\*\*/;
  const italicRegex = /^(?<!\*)\*([^*]+)\*(?!\*)/;

  while (i < text.length && visibleLength < maxLength) {
    const linkMatch = MARKDOWN_LINK_PATTERN_ANCHORED.exec(text.substring(i));
    const boldMatch = boldRegex.exec(text.substring(i));
    const italicMatch = italicRegex.exec(text.substring(i));

    if (linkMatch) {
      visibleLength += linkMatch[1].length;
      i += linkMatch[0].length;
    } else if (boldMatch) {
      visibleLength += boldMatch[1].length;
      i += boldMatch[0].length;
    } else if (italicMatch) {
      visibleLength += italicMatch[1].length;
      i += italicMatch[0].length;
    } else {
      visibleLength += 1;
      i += 1;
    }
  }

  return text.substring(0, i);
};
